import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SEOHead from '../components/SEOHead';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  MessageCircle,
  Calendar,
  CheckCircle,
  Globe,
  Building
} from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    budget: '',
    timeline: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Encode form data for Netlify
    const encode = (data) => {
      return Object.keys(data)
        .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
        .join("&");
    };
    
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact-page", ...formData })
      });
      
      if (response.ok) {
        toast.success('Votre demande a été envoyée avec succès! Nous vous recontacterons sous 24h.');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          budget: '',
          timeline: '',
          message: ''
        });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer ou nous contacter directement par téléphone.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Adresse",
      content: "Angré Face Soleil 3, immeuble restaurant Wallace\nAbidjan Cocody, Côte d'Ivoire",
      color: "orange"
    },
    {
      icon: Phone,
      title: "Téléphone",
      content: "+225 27 22 34 98 15\n+225 07 57 20 73 19",
      color: "blue"
    },
    {
      icon: Mail,
      title: "Email",
      content: "contact@set-g.com",
      color: "green"
    },
    {
      icon: Clock,
      title: "Horaires",
      content: "Du lundi au vendredi\nde 08h à 17h",
      color: "purple"
    }
  ];

  const whyChooseUs = [
    {
      icon: CheckCircle,
      title: "Réponse Rapide",
      description: "Réponse garantie sous 24h"
    },
    {
      icon: Globe,
      title: "Expertise Reconnue",
      description: `${new Date().getFullYear() - 2020} ans d'excellence, gérant 25+ ans d'expérience`
    },
    {
      icon: Building,
      title: "Clients Prestigieux",
      description: "ARTCI, MOOV, SIR..."
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      orange: 'bg-orange-100 text-orange-600',
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600'
    };
    return colors[color] || 'bg-gray-100 text-gray-600';
  };

  const siteUrl = "https://www.set-g.com";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${siteUrl}/contact#contactpage`,
    "name": "Contactez SET-G - Devis Gratuit Audit QoS & IT",
    "url": `${siteUrl}/contact`,
    "mainEntity": {
      "@type": "Organization",
      "name": "SET-G",
      "telephone": ["+225-27-22-34-98-15", "+225-07-57-20-73-19"],
      "email": "contact@set-g.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Angré Face Soleil 3, immeuble restaurant Wallace",
        "addressLocality": "Abidjan Cocody",
        "postalCode": "06 BP 2179 Abidjan 06",
        "addressCountry": "CI"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "5.3908181",
        "longitude": "-3.9750726"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "17:00"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+225-27-22-34-98-15",
          "contactType": "customer service",
          "availableLanguage": ["French"]
        },
        {
          "@type": "ContactPoint",
          "telephone": "+225-07-57-20-73-19",
          "contactType": "sales",
          "availableLanguage": ["French"]
        }
      ]
    }
  };

  return (
    <>
      <SEOHead
        title="Contact | Devis Gratuit ☎ +225 27 22 34 98 15"
        description="Contactez SET-G pour vos projets télécoms et IT. ☎ +225 27 22 34 98 15 | +225 07 57 20 73 19. Devis gratuit sous 24h. Adresse: Angré Face Soleil 3, Abidjan Cocody. Expert audit QoS, ingénierie radio."
        keywords="contact SET-G Abidjan, téléphone SET-G, devis audit QoS gratuit, expert télécommunications Côte d'Ivoire, Angré Face Soleil 3, consultation IT gratuite, rendez-vous télécoms"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MessageCircle className="w-4 h-4 mr-2" />
                Contactez-nous
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Démarrons Votre Projet
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                Êtes-vous prêt à optimiser vos réseaux et systèmes ? Contactez nos experts 
                pour un audit gratuit et des solutions personnalisées.
              </p>
              
              {/* Quick Stats */}
              <div className="grid md:grid-cols-3 gap-6 max-w-2xl mx-auto">
                {whyChooseUs.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="text-center">
                      <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-300">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card className="border border-slate-200 shadow-xl">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-3xl font-bold text-slate-900">
                      Demande de Devis Gratuit
                    </CardTitle>
                    <CardDescription className="text-lg text-slate-600">
                      Remplissez le formulaire ci-dessous et recevez une réponse personnalisée sous 24h.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form 
                      onSubmit={handleSubmit} 
                      name="contact-page" 
                      method="POST" 
                      data-netlify="true" 
                      data-netlify-honeypot="bot-field"
                      className="space-y-6"
                    >
                      {/* Hidden fields for Netlify */}
                      <input type="hidden" name="form-name" value="contact-page" />
                      <input type="hidden" name="bot-field" />
                      
                      {/* Personal Info */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Nom complet *
                          </label>
                          <Input 
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Votre nom et prénom"
                            required
                            className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Email professionnel *
                          </label>
                          <Input 
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="vous@entreprise.com"
                            required
                            className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                          />
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Entreprise/Organisation
                          </label>
                          <Input 
                            type="text"
                            value={formData.company}
                            onChange={(e) => handleInputChange('company', e.target.value)}
                            placeholder="Nom de votre entreprise"
                            className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Téléphone
                          </label>
                          <Input 
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder="+225 27 22 34 98 15"
                            className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                          />
                        </div>
                      </div>
                      
                      {/* Project Details */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Service d'intérêt *
                          </label>
                          <Select onValueChange={(value) => handleInputChange('service', value)} required>
                            <SelectTrigger className="border-slate-300 focus:border-orange-500 focus:ring-orange-500">
                              <SelectValue placeholder="Sélectionnez un service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="audit-qos">Audit & QoS des Réseaux</SelectItem>
                              <SelectItem value="ingenierie-radio">Ingénierie Radio</SelectItem>
                              <SelectItem value="infrastructure-it">Infrastructure IT</SelectItem>
                              <SelectItem value="formation">Formation & Conseil</SelectItem>
                              <SelectItem value="digitalisation">Digitalisation</SelectItem>
                              <SelectItem value="autre">Autre service</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Budget estimatif
                          </label>
                          <Select onValueChange={(value) => handleInputChange('budget', value)}>
                            <SelectTrigger className="border-slate-300 focus:border-orange-500 focus:ring-orange-500">
                              <SelectValue placeholder="Sélectionnez une fourchette" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="<10k">&lt; 10 000 €</SelectItem>
                              <SelectItem value="10k-50k">10 000 - 50 000 €</SelectItem>
                              <SelectItem value="50k-100k">50 000 - 100 000 €</SelectItem>
                              <SelectItem value=">100k">&gt; 100 000 €</SelectItem>
                              <SelectItem value="discuss">À définir</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Timeline souhaitée
                        </label>
                        <Select onValueChange={(value) => handleInputChange('timeline', value)}>
                          <SelectTrigger className="border-slate-300 focus:border-orange-500 focus:ring-orange-500">
                            <SelectValue placeholder="Quand souhaitez-vous démarrer ?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="immediate">Immédiatement</SelectItem>
                            <SelectItem value="1month">Dans le mois</SelectItem>
                            <SelectItem value="3months">Dans 3 mois</SelectItem>
                            <SelectItem value="6months">Dans 6 mois</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Message */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Décrivez votre projet *
                        </label>
                        <Textarea 
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          placeholder="Décrivez vos besoins, objectifs, contraintes techniques, environnement actuel..."
                          rows={6}
                          required
                          className="border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                        />
                      </div>
                      
                      {/* Submit Button */}
                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full bg-orange-600 hover:bg-orange-700 text-white py-4 text-lg font-medium rounded-lg group transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            Envoyer ma Demande
                            <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
              
              {/* Contact Information */}
              <div className="space-y-8">
                {/* Contact Details */}
                <Card className="border border-slate-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-slate-900">
                      Informations de Contact
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {contactInfo.map((info, index) => {
                      const Icon = info.icon;
                      return (
                        <div key={index} className="flex items-start space-x-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${getColorClasses(info.color)}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900 mb-1">{info.title}</h4>
                            <p className="text-slate-600 whitespace-pre-line">{info.content}</p>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
                
                {/* Map placeholder */}
                <Card className="border border-slate-200">
                  <CardContent className="p-0">
                    <div className="h-48 bg-slate-200 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-500">Carte Interactive</p>
                        <p className="text-sm text-slate-400">Angré Face Soleil 3, Abidjan</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Emergency Contact */}
                <Card className="bg-slate-900 text-white border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center">
                      <Phone className="w-5 h-5 mr-2" />
                      Urgence Technique ?
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300 mb-4">
                      Pour les interventions d'urgence sur vos réseaux, contactez notre 
                      équipe technique 24h/24.
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Urgence: +225 27 22 34 98 15
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Quick */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">
              Questions Fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-slate-900 mb-2">Combien coûte un audit QoS ?</h3>
                  <p className="text-slate-600">Les tarifs dépendent de l'étendue et la durée. Contactez-nous pour un devis personnalisé.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-slate-900 mb-2">Intervenez-vous en urgence ?</h3>
                  <p className="text-slate-600">Oui, nous avons une équipe d'urgence 24h/24 pour les pannes critiques.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;